import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getLogoByDomainAndAlt } from '../../utils/logoUtils';
import { toast } from 'react-toastify';
import { validateOTP, validateForm } from '../../utils/validation';
import { verifyRegisterOTPApi, resendOTPApi } from '../../services/authService';

function RegisterOTP() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const domain = window.location.origin;

  const [formData, setFormData] = useState({ otp: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const validationRules = { otp: validateOTP };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'otp' && !/^\d*$/.test(value)) return;
    setFormData({ ...formData, [name]: value.slice(0, 6) });
    setErrors({ ...errors, [name]: undefined });
  };

  const handleResendOTP = async () => {
    if (!email) {
      toast.error('Email not found. Please go back to login.', { position: "top-center", autoClose: 3000 });
      return;
    }

    setIsResending(true);
    try {
      const payload = { email: email, organization_url: domain };
      await resendOTPApi(payload);
      toast.success('OTP resent successfully!', { position: "top-center", autoClose: 2000 });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend OTP.', { position: "top-center", autoClose: 3000 });
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validateForm(formData, validationRules);
    if (validation.isValid) {
      setIsLoading(true);
      try {
        const payload = { email: email, domain: domain, otp: formData.otp };
        const response = await verifyRegisterOTPApi(payload);
        if (response.success) {
          toast.success('Registration completed successfully!', {
            position: "top-center",
            autoClose: 2000,
            onClose: () => navigate('/auth/login')
          });
        } else {
          throw new Error(response.message || 'Invalid OTP');
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to verify OTP.', { position: "top-center", autoClose: 2000 });
        setIsLoading(false);
      }
    } else {
      setErrors(validation.errors);
    }
  };

  return (
    <div className="min-h-screen bg-light-background flex flex-col items-center justify-center p-4 sm:p-6">
      {/* Logo */}
      <div className="mb-6">
        <img src={getLogoByDomainAndAlt().logo} alt={getLogoByDomainAndAlt().alt} className="h-10 w-auto" />
      </div>

      {/* Card */}
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-font-primary mb-2">Verify OTP</h2>
          <p className="text-font-secondary text-sm">Enter the 6-digit code sent to your email</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* OTP Field */}
          <div>
            <label htmlFor="otp" className="block text-sm font-medium text-font-primary mb-1.5">Enter OTP</label>
            <div className="relative">
              <Icon icon="mdi:key-outline" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                id="otp"
                name="otp"
                value={formData.otp}
                onChange={handleChange}
                placeholder="Enter 6-digit OTP"
                className={`w-full pl-10 pr-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors tracking-widest text-center font-mono ${errors.otp ? 'border-error' : 'border-input-border'}`}
                disabled={isLoading}
                maxLength={6}
              />
            </div>
            {errors.otp && <span className="text-error text-xs mt-1 block">{errors.otp[0]}</span>}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Verifying OTP...
              </>
            ) : (
              'Verify OTP'
            )}
          </button>

          {/* Options */}
          <div className="text-center text-sm text-font-secondary space-y-2">
            <p>
              Didn't receive the code?{' '}
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isLoading || isResending}
                className="text-primary hover:text-primary/80 font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isResending ? 'Sending...' : 'Resend OTP'}
              </button>
            </p>
            <p>
              Back to{' '}
              <Link 
                to="/auth/login" 
                className={`text-primary hover:text-primary/80 font-medium ${isLoading ? 'pointer-events-none opacity-60' : ''}`}
              >
                Login
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RegisterOTP;
